import React, { useState } from 'react';
import { action, observable, makeObservable } from 'mobx';
import { observer } from 'src/utils/mobx-react';
import { PaymentMethodDropdownState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/PaymentMethodDropdown.state';
import { WithdrawProcedure } from 'src/domains/players/webview/components/WithdrawAndDeposit/withdrawProcedure/withdraw/WithdrawProcedure';
import { WithdrawFormState } from 'src/domains/players/webview/components/WithdrawAndDeposit/withdrawProcedure/WithdrawFormState';
import { WithdrawIssue } from 'src/domains/players/webview/components/WithdrawAndDeposit/withdrawProcedure/withdrawProcedureParts/withdrawIssue/WithdrawIssue';
import { PendingWithdrawals, WithdrawProcedureWrapper } from 'src/domains/players/webview/components/Account';
import { WithdrawSuccess } from 'src/domains/players/webview/components/WithdrawAndDeposit/withdrawProcedure/withdrawProcedureParts/withdrawSuccess/WithdrawSuccess';
import { Amount } from 'src_common/common/amount/Amount';
import { useCommon } from 'src/domains/common/Common';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import {
    MessageIframeYaspaDataType,
    YaspaIframe,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaIframe';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { YaspaUrlResponseType } from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaSegment/YaspaSegment.state';
import { UsersState } from 'src/domains/players/state/UsersState';
import { WithdrawalsListState } from 'src/domains/players/state/WithdrawalsListState';

export type WithdrawStepsType =
    | {
          readonly type: 'set-card';
      }
    | {
          readonly type: 'iframe-view-yaspa';
          url: YaspaUrlResponseType['link'];
      }
    | {
          readonly type: 'failure-view';
          readonly failureType: WithdrawFailuresType;
      }
    | {
          readonly type: 'withdraw-pending';
      };

export type WithdrawFailuresType = 'serverIssue' | 'noPaymentMethods';

const defaultWithdrawStep: WithdrawStepsType = {
    type: 'set-card',
};

export class WithdrawSteps {
    @observable.ref public step: WithdrawStepsType;

    public constructor(public initialStep?: WithdrawStepsType) {
        makeObservable(this);
        this.step = initialStep ?? defaultWithdrawStep;
    }

    @action public redirectToSetCard = (): void => {
        this.step = {
            type: 'set-card',
        };
    };

    @action public redirectToIframeYaspa = (link: YaspaUrlResponseType['link']): void => {
        this.step = {
            type: 'iframe-view-yaspa',
            url: link,
        };
    };

    @action public redirectToFailureView = (failureType: WithdrawFailuresType): void => {
        this.step = {
            type: 'failure-view',
            failureType,
        };
    };

    @action public redirectToPending = (): void => {
        this.step = {
            type: 'withdraw-pending',
        };
    };

    @action public redirectFromYaspaIframe = (data?: MessageIframeYaspaDataType): void => {
        if (data?.eventType === 'onCloseHiddenIframeFail') {
            this.step = {
                type: 'failure-view',
                failureType: 'serverIssue',
            };
        } else if (data?.eventType === 'onCloseHiddenIframeSuccess') {
            this.step = {
                type: 'withdraw-pending',
            };
        }
    };
}

export const WithdrawJourney = observer('WithdrawJourney', (): JSX.Element => {
    const common = useCommon();
    const withdrawalsListState = WithdrawalsListState.get(common);
    const usersState = UsersState.get(common);
    const googleTagManager = GoogleTagManagerState.get(common);
    const languagesState = LanguagesState.get(common);
    const basicDataModel = BasicDataModel.get(common);
    const {
        config: { minWithdrawAmount },
        precision,
    } = ConfigComponents.get(common);

    const [paymentMethodDropdownState] = useState(
        () => new PaymentMethodDropdownState(common.trpcClient, basicDataModel, common)
    );
    const [state] = React.useState(
        () =>
            new WithdrawFormState(
                common.trpcClient,
                usersState,
                languagesState,
                withdrawalsListState,
                googleTagManager,
                paymentMethodDropdownState,
                new Amount(minWithdrawAmount),
                precision,
                common
            )
    );

    switch (state.currentStep.type) {
        case 'set-card':
            return (
                <>
                    <WithdrawProcedureWrapper data-test='withdraw-procedure-wrapper'>
                        <WithdrawProcedure
                            state={state}
                            paymentMethodDropdownState={paymentMethodDropdownState}
                        />
                    </WithdrawProcedureWrapper>
                    <PendingWithdrawals />
                </>
            );
        case 'iframe-view-yaspa':
            return (
                <YaspaIframe
                    src={state.currentStep.url}
                    onMessage={state.stepsState.redirectFromYaspaIframe}
                />
            );
        case 'failure-view':
            return <WithdrawIssue failureType={state.currentStep.failureType} />;
        case 'withdraw-pending':
            return <WithdrawSuccess />;
    }
});
